import { useQuery } from '@tanstack/react-query'
import { apiBaseUrl } from 'constants/apiBaseUrl'
import { getHeaders } from '@/core/getHeaders'
import { OLSPricingModel } from '@otw/models/ols'

/**
 * Fetches a single OLS pricing model by ID.
 * Simpler alternative to useOLSCategories when only one model is needed.
 */
export const useOLSModel = (categoryId: string) => {
  const {
    data: model,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<OLSPricingModel | null>({
    queryKey: ['ols_model', categoryId],
    queryFn: async () => {
      const headers = await getHeaders()
      const response = await fetch(`${apiBaseUrl}/portal/pricing/ols/categories`, {
        method: 'GET',
        headers,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch OLS model')
      }

      const result = await response.json()
      const categories = result.categories as OLSPricingModel[]
      return categories.find(c => c.id === categoryId) ?? null
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return {
    model: model ?? null,
    isLoading,
    isError,
    error,
    refetch,
  }
}
